<!-- compact single-NOTAM mode (trajectory map info) -->
<div *ngIf="single" class="notam-item notam-single">
	<div class="notam-item-header">
		<span class="notam-dot" [ngClass]="severityClass(single)"></span>
		<span class="notam-category">{{ 'NOTAM.CATEGORY.' + single.category | translate | capitalize }}</span>
		<span class="notam-designator notranslate">{{ single.designator }}</span>
	</div>
	<div class="notam-item-line">
		<mat-icon class="notam-line-icon">schedule</mat-icon>
		<span class="notranslate">{{ periodLabel(single) }}</span>
	</div>
	<div *ngIf="single.schedule" class="notam-item-line">
		<mat-icon class="notam-line-icon">event_repeat</mat-icon>
		<span class="notam-schedule notranslate">{{ single.schedule }}</span>
	</div>
	<div class="notam-item-line">
		<mat-icon class="notam-line-icon">height</mat-icon>
		<span class="notranslate">{{ altitudeBand(single) }}</span>
	</div>
	<div *ngIf="single.distanceKm != null" class="notam-item-line">
		<mat-icon class="notam-line-icon">near_me</mat-icon>
		<span class="notranslate">{{ distanceLabel(single) }}</span>
	</div>
	<pre class="notam-raw-text notranslate">{{ single.text }}</pre>
	<!-- freshness + PIC disclaimer also here: FR-007 applies to every NOTAM display -->
	<div class="notam-footer">
		<span *ngIf="updatedAt">{{ 'NOTAM.updated' | translate: { time: freshnessLabel() } }}</span>
		<span class="notam-disclaimer">{{ 'NOTAM.disclaimer' | translate }}</span>
	</div>
</div>

<!-- full assessment mode -->
<div *ngIf="!single && assessment" class="notam-list">
	<!-- explicit stale state (FR-008) -->
	<div *ngIf="assessment.stale" class="notam-banner notam-banner-stale">
		<mat-icon>history</mat-icon>
		<span *ngIf="assessment.updatedAt">{{ 'NOTAM.stale' | translate: { time: freshnessLabel() } }}</span>
		<span *ngIf="!assessment.updatedAt">{{ 'NOTAM.unavailable' | translate }}</span>
	</div>

	<!-- location without coordinates (edge case) -->
	<div *ngIf="assessment.noData" class="notam-banner notam-banner-muted">
		<mat-icon>location_off</mat-icon>
		<span>{{ 'NOTAM.no_data' | translate }}</span>
	</div>

	<!-- quiet empty state -->
	<div *ngIf="!assessment.noData && isEmpty && !assessment.stale" class="notam-banner notam-banner-muted">
		<mat-icon>check_circle_outline</mat-icon>
		<span
			>{{ 'NOTAM.none_known' | translate }}<ng-container *ngIf="beyondNotamHorizon"> — {{ 'NOTAM.horizon_hint' | translate }}</ng-container></span
		>
	</div>

	<!-- NOTAM list, severity-sorted -->
	<div *ngFor="let notam of sortedNotams" class="notam-item">
		<div class="notam-item-header">
			<span class="notam-dot" [ngClass]="severityClass(notam)"></span>
			<span class="notam-category">{{ 'NOTAM.CATEGORY.' + notam.category | translate | capitalize }}</span>
			<span class="notam-designator notranslate">{{ notam.designator }}</span>
			<span *ngIf="notam.distanceKm != null" class="notam-distance notranslate">{{ distanceLabel(notam) }}</span>
		</div>
		<div class="notam-item-line">
			<mat-icon class="notam-line-icon" [matTooltip]="'NOTAM.period' | translate | capitalize">schedule</mat-icon>
			<span class="notranslate">{{ periodLabel(notam) }}</span>
		</div>
		<div *ngIf="notam.schedule" class="notam-item-line">
			<mat-icon class="notam-line-icon" [matTooltip]="'NOTAM.schedule' | translate | capitalize">event_repeat</mat-icon>
			<span class="notam-schedule notranslate">{{ notam.schedule }}</span>
		</div>
		<div class="notam-item-line">
			<mat-icon class="notam-line-icon" [matTooltip]="'NOTAM.altitude_band' | translate | capitalize">height</mat-icon>
			<span class="notranslate">{{ altitudeBand(notam) }}</span>
		</div>
		<button mat-button class="notam-text-toggle" (click)="toggleText(notam.id)">
			<mat-icon>{{ expandedTextIds.has(notam.id) ? 'expand_less' : 'expand_more' }}</mat-icon>
			{{ (expandedTextIds.has(notam.id) ? 'NOTAM.hide_text' : 'NOTAM.show_text') | translate | capitalize }}
		</button>
		<pre *ngIf="expandedTextIds.has(notam.id)" class="notam-raw-text notranslate">{{ notam.text }}</pre>
	</div>

	<!-- collapsed wide-area group (FR-010) -->
	<div *ngIf="wideAreaNotams.length > 0" class="notam-wide-area">
		<button mat-button (click)="wideAreaExpanded = !wideAreaExpanded">
			<mat-icon>{{ wideAreaExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
			{{ 'NOTAM.wide_area' | translate | capitalize }} ({{ wideAreaNotams.length }})
		</button>
		<div *ngIf="wideAreaExpanded">
			<div *ngFor="let notam of wideAreaNotams" class="notam-item notam-item-wide">
				<div class="notam-item-header">
					<span class="notam-dot notam-amber"></span>
					<span class="notam-category">{{ 'NOTAM.CATEGORY.' + notam.category | translate | capitalize }}</span>
					<span class="notam-designator notranslate">{{ notam.designator }}</span>
				</div>
				<div class="notam-item-line">
					<mat-icon class="notam-line-icon">schedule</mat-icon>
					<span class="notranslate">{{ periodLabel(notam) }}</span>
				</div>
				<button mat-button class="notam-text-toggle" (click)="toggleText(notam.id)">
					<mat-icon>{{ expandedTextIds.has(notam.id) ? 'expand_less' : 'expand_more' }}</mat-icon>
					{{ (expandedTextIds.has(notam.id) ? 'NOTAM.hide_text' : 'NOTAM.show_text') | translate | capitalize }}
				</button>
				<pre *ngIf="expandedTextIds.has(notam.id)" class="notam-raw-text notranslate">{{ notam.text }}</pre>
			</div>
		</div>
	</div>

	<!-- freshness + PIC disclaimer (FR-007) -->
	<div class="notam-footer">
		<span *ngIf="assessment.updatedAt">{{ 'NOTAM.updated' | translate: { time: freshnessLabel() } }}</span>
		<span class="notam-disclaimer">{{ 'NOTAM.disclaimer' | translate }}</span>
	</div>
</div>
